import { Badge, cn } from '@startupquickstart/frontend';
import { ImageOff, MessageSquareText, Play, Sparkles } from 'lucide-react';

import {
  SUBMISSION_STATUS_META,
  formatRelativeTime,
} from '@/components/submissions/submissionStatus';
import { PHOTO_STATUS, type Submission } from '@/components/submissions/useSubmissions';
import { challengeMediaType, isVideoProofUrl } from '@/lib/media';

/** Micro-label chrome reused across the submissions surface. */
const MICRO_LABEL = 'font-mono text-[0.6875rem] tracking-[0.15em] uppercase text-muted-foreground';

interface SubmissionCardProps {
  submission: Submission;
  /** Opens the review drawer positioned on this submission. */
  onOpen: (submission: Submission) => void;
}

/**
 * Renders the AI confidence as a labelled progress bar plus the reasoning
 * line. Rendered only when the submission carries a confidence score.
 */
function AiConfidence({ confidence, reasoning }: { confidence: number; reasoning: string | null }) {
  // API stores confidence as a 0–1 float; render as a whole percentage.
  const pct = Math.round(Math.min(Math.max(confidence, 0), 1) * 100);
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <span className={cn(MICRO_LABEL, 'flex items-center gap-1')}>
          <Sparkles className="h-3 w-3" aria-hidden="true" /> AI confidence
        </span>
        <span className="font-mono text-xs font-medium text-foreground">{pct}%</span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full bg-primary transition-all"
          style={{ width: `${pct}%` }}
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
        />
      </div>
      {reasoning && <p className="line-clamp-2 text-xs text-muted-foreground">{reasoning}</p>}
    </div>
  );
}

/**
 * A single submission tile in the gallery grid: photo thumbnail (or an
 * answer-text block for text submissions), team + challenge, relative time,
 * status badge, and an optional AI confidence bar. Clicking opens the review
 * drawer.
 */
export function SubmissionCard({ submission, onOpen }: SubmissionCardProps) {
  const status = submission.photoStatus ?? PHOTO_STATUS.pending;
  const meta = SUBMISSION_STATUS_META[status];
  const StatusIcon = meta.icon;
  const hasPhoto = Boolean(submission.photoProofUrl);
  const isVideo =
    hasPhoto &&
    isVideoProofUrl(submission.photoProofUrl, challengeMediaType(submission.challenge));

  return (
    <button
      type="button"
      onClick={() => onOpen(submission)}
      className="flex h-full flex-col overflow-hidden rounded-xl border bg-card text-left shadow-sm transition-shadow hover:shadow-md focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
    >
      <div className="relative aspect-video w-full overflow-hidden bg-muted">
        {hasPhoto && isVideo ? (
          <>
            {/* Muted first-frame preview; playback happens in the drawer. */}
            <video
              src={submission.photoProofUrl ?? undefined}
              muted
              playsInline
              preload="metadata"
              className="h-full w-full object-cover"
              aria-label={`${submission.team.name} — ${submission.challenge.name} (video)`}
            />
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-black/60 text-white shadow">
                <Play className="h-5 w-5" aria-hidden="true" />
              </span>
            </div>
          </>
        ) : hasPhoto ? (
          <img
            src={submission.photoProofUrl ?? undefined}
            alt={`${submission.team.name} — ${submission.challenge.name}`}
            className="h-full w-full object-cover"
            loading="lazy"
          />
        ) : submission.answer ? (
          <div className="flex h-full w-full flex-col items-start justify-center gap-2 p-4">
            <MessageSquareText className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            <p className="line-clamp-3 text-sm font-medium text-foreground">
              &ldquo;{submission.answer}&rdquo;
            </p>
          </div>
        ) : (
          <div className="flex h-full w-full items-center justify-center text-muted-foreground">
            <ImageOff className="h-6 w-6" aria-hidden="true" />
          </div>
        )}
        <div className="absolute top-2 right-2">
          <Badge variant={meta.badgeVariant} className="gap-1">
            <StatusIcon className="h-3 w-3" aria-hidden="true" />
            {meta.label}
          </Badge>
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-2 p-4">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-foreground">{submission.team.name}</p>
          <p className="truncate text-xs text-muted-foreground">{submission.challenge.name}</p>
        </div>

        {submission.aiConfidence != null && (
          <AiConfidence confidence={submission.aiConfidence} reasoning={submission.aiReasoning} />
        )}

        <div className="mt-auto flex items-center justify-between pt-1">
          <span className="text-xs text-muted-foreground">
            {formatRelativeTime(submission.submittedAt)}
          </span>
          <span className="font-mono text-xs text-muted-foreground">
            {submission.challenge.points} pts
          </span>
        </div>
      </div>
    </button>
  );
}
